import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { AppUser, PolicyConsentVersions, PolicyDocument, PolicyType } from '../types';
import { refreshSecurityToken } from './securityClaims';
import { withReadCounter } from '../utils/withReadCounter';

export const POLICY_TYPES: PolicyType[] = ['user', 'marketing', 'data', 'pricing'];

export const DEFAULT_POLICIES: Record<PolicyType, PolicyDocument> = {
  user: {
    id: 'user',
    type: 'user',
    title: 'User Policy',
    version: '1.0',
    effectiveDate: '2026-09-14',
    contentHi: `प्रभावी तिथि: 14-09-2026\nसंस्करण: 1.0\n\nYeTryKar इस User Policy को कभी भी अपडेट या बदल सकता है। ऊपर दिया गया संस्करण और प्रभावी तिथि आपके सहमत होने के समय लागू होगी। आपको सही खाता जानकारी देनी होगी, अपना खाता सुरक्षित रखना होगा और सेवा का कानूनी उपयोग करना होगा। आपके द्वारा अपलोड की गई सामग्री का स्वामित्व आपका रहेगा, लेकिन YeTryKar को उसे सेवा पर होस्ट, दिखाने, प्रोसेस और वितरित करने की अनुमति मिलेगी।\n\nAll rights reserved to YeTryKar — प्लेटफॉर्म सामग्री, ब्रांडिंग और मूल तकनीक के सभी अधिकार YeTryKar के पास सुरक्षित हैं।\n\nहम आपका नाम, यूज़रनेम, ईमेल, फोन, पता, प्रोफाइल फोटो, उम्र, लिंग, लॉगिन जानकारी, अपलोड की गई सामग्री, कैप्शन, लाइक, व्यू, कमेंट, फॉलो, नोटिफिकेशन, संदेश और डिवाइस/ऐप की तकनीकी जानकारी एकत्र कर सकते हैं।`,
    content: `Effective date: 2026-09-14\nVersion: 1.0\n\nYeTryKar may update this User Policy at any time. The current version and effective date shown above apply when you agree. You are responsible for accurate account information, keeping your credentials secure, and lawful use of the service. You retain ownership of content you upload, while granting YeTryKar the permission required to host, display, process, and distribute it through the service.\n\nWe may remove content or restrict accounts that violate this policy or applicable law. All rights reserved to YeTryKar for platform content, branding, and underlying technology.\n\nData collected for this policy includes your display name, username, email, phone number, address, profile photo, age, gender, account timestamps, authentication provider, uploaded media and captions, likes, views, comments, follows, notifications, messages, and suspension records. Device and diagnostic information may include browser or app platform, device model, operating system, IP-derived security signals, and error logs.`
  },
  marketing: {
    id: 'marketing',
    type: 'marketing',
    title: 'Marketing Policy',
    version: '1.0',
    effectiveDate: '2026-09-14',
    contentHi: `प्रभावी तिथि: 14-09-2026\nसंस्करण: 1.0\n\nYeTryKar इस Marketing Policy को कभी भी अपडेट या बदल सकता है। हम आपके नाम, ईमेल, फोन, नोटिफिकेशन पसंद, फॉलो किए गए क्रिएटर, देखी या पसंद की गई सामग्री और सदस्यता स्थिति का उपयोग प्रचार, सेवा सूचना और ईमेल भेजने के लिए कर सकते हैं। प्रचार संदेश बंद करने का विकल्प उपलब्ध रहेगा; आवश्यक सुरक्षा और खाता संदेश फिर भी आ सकते हैं। All rights reserved to YeTryKar।`,
    content: `Effective date: 2026-09-14\nVersion: 1.0\n\nYeTryKar may update this Marketing Policy at any time. The current version and effective date shown above apply when you agree. We may use your display name, email address, phone number, notification preferences, followed creators, viewed or liked content, and subscription or transaction status to send product announcements, promotions, service notifications, and emails. We do not sell personal information.\n\nMarketing providers may process delivery metadata such as email delivery, open, click, push-token, and unsubscribe events. You can opt out of promotional messages while still receiving essential account and security notifications. All rights reserved to YeTryKar for platform content, branding, and underlying technology.`
  },
  data: {
    id: 'data',
    type: 'data',
    title: 'Data Policy',
    version: '1.0',
    effectiveDate: '2026-09-14',
    contentHi: `प्रभावी तिथि: 14-09-2026\nसंस्करण: 1.0\n\nYeTryKar इस Data Policy को कभी भी अपडेट या बदल सकता है। हम Firebase user ID, नाम, यूज़रनेम, ईमेल, फोन, पता, फोटो, उम्र, लिंग, रोल, लॉगिन समय, प्रोफाइल स्थिति, अपलोडेड मीडिया, कैप्शन, हैशटैग, व्यू, लाइक, कमेंट, फॉलो, संदेश, नोटिफिकेशन, रिपोर्ट और डिवाइस/ऐप की तकनीकी जानकारी एकत्र करते हैं। डेटा Firebase Authentication, Firestore, Storage और सेवा चलाने वाले आवश्यक प्रदाताओं में रखा जाता है। All rights reserved to YeTryKar।`,
    content: `Effective date: 2026-09-14\nVersion: 1.0\n\nYeTryKar may update this Data Policy at any time. The current version and effective date shown above apply when you agree. We collect: Firebase user ID, display name, username, email, phone number, address, photo URL, age, gender, role, permissions, clue ID, login method, account creation and login timestamps, profile-completion state, suspension data, uploaded media URLs and thumbnails, titles, captions, hashtags, media type, duration, file size, views, likes, comments, shares, follow relationships, chat messages and timestamps, notifications, reports, and audit records. We also collect device/app platform, browser or operating system, device model where available, IP-derived security signals, crash and diagnostic logs, and storage/cache metadata.\n\nData is stored in Firebase Authentication, Firestore, Firebase Storage, and notification or analytics providers needed to operate the service. We retain data while your account is active and as needed for safety, fraud prevention, dispute handling, backups, and legal obligations. All rights reserved to YeTryKar for platform content, branding, and underlying technology.`
  },
  pricing: {
    id: 'pricing',
    type: 'pricing',
    title: 'Pricing Policy',
    version: '1.0',
    effectiveDate: '2026-09-14',
    contentHi: `प्रभावी तिथि: 14-09-2026\nसंस्करण: 1.0\n\nYeTryKar इस Pricing Policy को कभी भी अपडेट या बदल सकता है। सदस्यता, कमाई, ऑफर, टैक्स, बिलिंग चक्र, नवीनीकरण और रिफंड की शर्तें खरीद से पहले दिखाई जाएंगी। कीमत या सुविधाएं बदल सकती हैं और बदलाव नवीनीकरण से पहले बताए जाएंगे। हम खरीद, सदस्यता, बिलिंग स्थिति, रिफंड और सहायता रिकॉर्ड रखते हैं; कार्ड नंबर YeTryKar में सेव नहीं होते। All rights reserved to YeTryKar।`,
    content: `Effective date: 2026-09-14\nVersion: 1.0\n\nYeTryKar may update this Pricing Policy at any time. The current version and effective date shown above apply when you agree. Paid subscriptions, creator monetization, promotional offers, taxes, billing cycles, renewal dates, payment status, and refund eligibility will be shown before purchase. Prices, features, limits, and billing providers may change; material changes will be disclosed before they apply to a renewal or new purchase.\n\nRefunds are handled according to the applicable store or payment provider rules and any offer-specific terms. We collect purchase, subscription, billing-status, invoice, refund, and customer-support records, but payment card numbers are processed by the payment provider and are not stored by YeTryKar. All rights reserved to YeTryKar for platform content, branding, and underlying technology.`
  }
};

const asPolicy = (id: PolicyType, data?: Record<string, any>): PolicyDocument => ({
  ...DEFAULT_POLICIES[id],
  ...(data || {}),
  id,
  type: id,
  title: data?.title || DEFAULT_POLICIES[id].title,
  version: data?.version || DEFAULT_POLICIES[id].version,
  content: data?.content || DEFAULT_POLICIES[id].content,
  contentHi: data?.contentHi || DEFAULT_POLICIES[id].contentHi
});

export const getPolicies = async (): Promise<PolicyDocument[]> => {
  try {
    const snapshot = await withReadCounter(
      'policies',
      () => getDocs(query(collection(db, 'policies'), limit(POLICY_TYPES.length))),
      result => ({ reads: result.metadata.fromCache ? 0 : result.size })
    );
    const byId = new Map(snapshot.docs.map(item => [item.id as PolicyType, item.data()]));
    return POLICY_TYPES.map(id => asPolicy(id, byId.get(id)));
  } catch (error) {
    console.warn('Could not load policies from Firestore:', error);
    return POLICY_TYPES.map(id => DEFAULT_POLICIES[id]);
  }
};

export const seedDefaultPoliciesForSuperAdmin = async (): Promise<void> => {
  if (!auth.currentUser) return;
  const snapshot = await withReadCounter(
    'policies',
    () => getDocs(query(collection(db, 'policies'), limit(POLICY_TYPES.length))),
    result => ({ reads: result.metadata.fromCache ? 0 : result.size })
  );
  const existing = new Set(snapshot.docs.map(item => item.id));
  for (const id of POLICY_TYPES) {
    if (existing.has(id)) continue;
    const policy = DEFAULT_POLICIES[id];
    await setDoc(doc(db, 'policies', id), {
      type: id,
      title: policy.title,
      version: policy.version,
      content: policy.content,
      contentHi: policy.contentHi,
      effectiveDate: policy.effectiveDate,
      lastUpdatedBy: auth.currentUser.uid,
      updatedAt: serverTimestamp()
    });
    await addDoc(collection(db, 'policyEditLog'), {
      policyType: id,
      oldVersion: null,
      newVersion: policy.version,
      editedBy: auth.currentUser.uid,
      timestamp: serverTimestamp()
    });
  }
};

export const hasCurrentPolicyConsent = (
  user: Pick<AppUser, 'policyConsentVersions'> | null | undefined,
  policies: PolicyDocument[] = POLICY_TYPES.map(id => DEFAULT_POLICIES[id])
): boolean => {
  if (!user?.policyConsentVersions) return false;
  return policies.every(policy => user.policyConsentVersions?.[policy.id] === policy.version);
};

export const hasPolicyConsentRecord = (
  user: Pick<AppUser, 'policyConsentVersions'> | null | undefined
): boolean => POLICY_TYPES.every(policyId => Boolean(user?.policyConsentVersions?.[policyId]));

export const agreeToPolicies = async (
  uid: string,
  policies: PolicyDocument[],
  platform: 'web' | 'android' = 'web'
): Promise<PolicyConsentVersions> => {
  if (!auth.currentUser || auth.currentUser.uid !== uid) throw new Error('You must be signed in to agree to policies.');
  const versions = policies.reduce<PolicyConsentVersions>((result, policy) => {
    result[policy.id] = policy.version;
    return result;
  }, {});
  // Write each immutable audit entry first. Keeping this separate from the
  // user profile update avoids rules evaluating consent against a stale batch
  // state during the initial agreement.
  const agreementBatch = writeBatch(db);
  for (const policy of policies) {
    agreementBatch.set(doc(collection(db, 'policyAgreements')), {
      userId: uid,
      policyType: policy.type,
      policyVersion: policy.version,
      platform,
      timestamp: serverTimestamp()
    });
  }
  try {
    await withReadCounter('policy-consent', () => agreementBatch.commit(), { writes: policies.length });
  } catch (error) {
    console.error('Could not save policy agreement audit records:', error);
    throw new Error(`Could not save policy agreement records: ${error instanceof Error ? error.message : String(error)}`);
  }
  // Merge instead of update so first-time users whose profile document has
  // not been created yet can complete consent without a missing-document
  // permission failure.
  try {
    await withReadCounter(
      'policy-consent',
      () => setDoc(doc(db, 'users', uid), {
        policyConsentVersions: versions,
        policyConsentUpdatedAt: serverTimestamp()
      }, { merge: true }),
      { writes: 1 }
    );
  } catch (error) {
    console.error('Could not save policy consent to the user profile:', error);
    throw new Error(`Could not save policy consent to your profile: ${error instanceof Error ? error.message : String(error)}`);
  }
  try {
    await refreshSecurityToken(auth.currentUser, true);
  } catch (error) {
    console.error('Policy consent was saved, but security claims could not be refreshed:', error);
    throw new Error(`Your consent was saved, but security access could not be refreshed: ${error instanceof Error ? error.message : String(error)}`);
  }
  return versions;
};

export const savePolicy = async (
  policy: Pick<PolicyDocument, 'id' | 'title' | 'version' | 'content' | 'contentHi'>
): Promise<void> => {
  if (!auth.currentUser) throw new Error('Not authenticated.');
  const previous = (await getPolicies()).find(item => item.id === policy.id);
  const policyRef = doc(db, 'policies', policy.id);
  const batch = writeBatch(db);
  batch.set(policyRef, {
    title: policy.title.trim(),
    version: policy.version.trim(),
    content: policy.content.trim(),
    contentHi: policy.contentHi?.trim() || '',
    type: policy.id,
    effectiveDate: new Date().toISOString().slice(0, 10),
    lastUpdatedBy: auth.currentUser.uid,
    updatedAt: serverTimestamp()
  }, { merge: true });
  batch.set(doc(collection(db, 'policyEditLog')), {
    policyType: policy.id,
    oldVersion: previous?.version || null,
    newVersion: policy.version.trim(),
    editedBy: auth.currentUser.uid,
    timestamp: serverTimestamp()
  });
  await batch.commit();
};

export const getPolicyAgreementLogs = async (): Promise<any[]> => {
  const snapshot = await getDocs(query(collection(db, 'policyAgreements'), orderBy('timestamp', 'desc'), limit(100)));
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
};

export const getPolicyEditLogs = async (): Promise<any[]> => {
  const snapshot = await getDocs(query(collection(db, 'policyEditLog'), orderBy('timestamp', 'desc'), limit(100)));
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
};
